// ============================================
// Lab Session Firestore Service
//
// Tracks the lifecycle of lab uploads:
// uploaded → extracting → extracted → analyzing → complete | failed
//
// Collection: lab_sessions/{sessionId}
// ============================================

import {
  collection, doc, getDoc, getDocs, setDoc, updateDoc,
  query, where, orderBy, limit as firestoreLimit,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import type { LabSession, LabSessionStatus } from '../../types/labUpload';

import { C_LAB_SESSIONS } from '../../constants/collections';
// ── Collection ref ──

const labSessionsCol = () => collection(db, C_LAB_SESSIONS);

// ── CRUD ──

/** Create a new lab session. Returns the sessionId. */
export async function createLabSession(
  session: LabSession,
): Promise<string> {
  const id = session.sessionId;

  await setDoc(doc(labSessionsCol(), id), {
    ...stripUndefined(session),
    _updatedAt: Timestamp.now(),
  });

  return id;
}

/** Fetch a single lab session by ID. */
export async function getLabSession(
  sessionId: string,
): Promise<LabSession | null> {
  const snap = await getDoc(doc(labSessionsCol(), sessionId));
  return snap.exists() ? (snap.data() as LabSession) : null;
}

/** Update lab session status with optional additional fields. */
export async function updateLabSessionStatus(
  sessionId: string,
  status: LabSessionStatus,
  updates?: Partial<LabSession>,
): Promise<void> {
  await updateDoc(doc(labSessionsCol(), sessionId), {
    ...stripUndefined(updates ?? {}),
    status,
    _updatedAt: Timestamp.now(),
  });
}

/** Get lab sessions for a specific patient, ordered by uploadedAt desc. */
export async function getLabSessionsForPatient(
  patientId: string,
  limit?: number,
): Promise<LabSession[]> {
  const constraints: Parameters<typeof query>[1][] = [
    where('patientId', '==', patientId),
    orderBy('uploadedAt', 'desc'),
  ];

  if (limit) {
    constraints.push(firestoreLimit(limit));
  }

  const snap = await getDocs(query(labSessionsCol(), ...constraints));
  return snap.docs.map((d) => d.data() as LabSession);
}

/** Get all lab sessions for a brand, ordered by uploadedAt desc. */
export async function getLabSessionsForBrand(
  brandId: string,
  limit?: number,
): Promise<LabSession[]> {
  const constraints: Parameters<typeof query>[1][] = [
    where('brandId', '==', brandId),
    orderBy('uploadedAt', 'desc'),
  ];

  if (limit) {
    constraints.push(firestoreLimit(limit));
  }

  const snap = await getDocs(query(labSessionsCol(), ...constraints));
  return snap.docs.map((d) => d.data() as LabSession);
}

// ── Utils ──

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function stripUndefined(obj: any): any {
  if (obj === null || obj === undefined || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(stripUndefined);
  const cleaned: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined) {
      cleaned[key] = (value !== null && typeof value === 'object')
        ? stripUndefined(value)
        : value;
    }
  }
  return cleaned;
}
